import { useEffect, useState } from "react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { KineticLines, SectionHead } from "@/components/Motion";
import { CategoryCard, ProductCard } from "@/components/Cards";
import CTASection from "@/components/CTASection";
import api from "@/lib/api";

export default function Products() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState("all");

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const filtered = active === "all" ? products : products.filter((p) => p.category === active);

  return (
    <div data-testid="products-page">
      <SEO title="Products" path="/products"
        description="Pharmaceutical cleanroom equipment from STJ Innovations: pass boxes, laminar air flow units, cleanroom doors and panels, furniture, booths, IoT solutions and precision sheet metal." />
      <section className="blueprint-dark bg-ink">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <Breadcrumbs dark items={[{ label: "Products" }]} />
          <p className="eyebrow eyebrow-light">Products</p>
          <KineticLines
            lines={["Cleanroom Equipment &", "Precision Engineering"]}
            className="mt-5 max-w-4xl font-display text-4xl font-medium leading-[1.06] tracking-tight text-white sm:text-5xl"
          />
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400">
            Every product is engineered to order or configured from proven platforms. Share your specification — we will develop the right solution.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
        <SectionHead eyebrow="Categories" title="Browse by Product Family" />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((c, i) => <CategoryCard key={c.slug} cat={c} index={i} />)}
        </div>
      </section>

      <section className="border-t border-line bg-mist">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <SectionHead eyebrow="Catalogue" title="All Products" />
          <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="Filter products by category">
            <button
              data-testid="filter-all"
              role="tab" aria-selected={active === "all"}
              onClick={() => setActive("all")}
              className={`border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${active === "all" ? "border-accent bg-accent text-white" : "border-line bg-white text-steel hover:border-accent/50"}`}
            >
              All
            </button>
            {categories.map((c) => (
              <button
                key={c.slug}
                data-testid={`filter-${c.slug}`}
                role="tab" aria-selected={active === c.slug}
                onClick={() => setActive(c.slug)}
                className={`border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${active === c.slug ? "border-accent bg-accent text-white" : "border-line bg-white text-steel hover:border-accent/50"}`}
              >
                {c.name}
              </button>
            ))}
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="products-grid">
            {filtered.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
